import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import ProgressBar from '../components/ProgressBar';
import Calendar from '../components/Calendar';
import DateSelector from '../components/DateSelector';
import TimeSlotSelector from '../components/TimeSlotSelector';
import ErrorModal from '../components/ErrorModal';
import { AppointmentTime } from '../services/types';
import { useAppState } from '../state/AppState';
import { getAvailabilitiesForScheduling } from '../services/availabilitiesService';
import { getProviderNameFromAvailability } from '../services/availabilityDataService';
import { setTimerStart, blockAppointment, fetchBlockHolds } from '../services/sessionService';
import { getConfig } from '../services/configService';
import { exclusionOverlapsSlot } from '../utils/recurrenceUtils';

/**
 * Converts a day name (e.g., "Thursday") to a day of week number (0-6, where 0 is Sunday)
 */
function getDayOfWeekNumber(dayName: string): number | null {
  const dayMap: { [key: string]: number } = {
    'Sunday': 0,
    'Monday': 1,
    'Tuesday': 2,
    'Wednesday': 3,
    'Thursday': 4,
    'Friday': 5,
    'Saturday': 6
  };
  
  const normalizedDayName = dayName.trim();
  return dayMap[normalizedDayName] !== undefined ? dayMap[normalizedDayName] : null;
}


/**
 * Creates a time range string in the format "12:30pm - 1:30pm @ Location"
 * or "12pm - 1pm @ Location" for exact hours
 */
function createTimeRange(startTime: string, location: string): string {
  // Parse the start time (assuming format like "14:30" or "9:00")
  const [hours, minutes] = startTime.split(':').map(Number);
  
  // Convert to 12-hour format
  const startHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const startAmPm = hours < 12 ? 'am' : 'pm';
  
  // Calculate end time (1 hour later)
  const endHours = hours + 1;
  const endHour = endHours === 0 ? 12 : endHours > 12 ? endHours - 12 : endHours;
  const endAmPm = endHours < 12 ? 'am' : 'pm';
  
  // Format start time (no :00 for exact hours)
  const startTimeStr = minutes === 0 ? `${startHour}${startAmPm}` : `${startHour}:${minutes.toString().padStart(2, '0')}${startAmPm}`;
  
  // Format end time (no :00 for exact hours)
  const endTimeStr = minutes === 0 ? `${endHour}${endAmPm}` : `${endHour}:${minutes.toString().padStart(2, '0')}${endAmPm}`;
  
  return `${startTimeStr} - ${endTimeStr}`;
}

/**
 * Parses a reserve time string (e.g. "3pm - 4pm @ Location") to HH:MM (24-hour).
 */
function parseReserveTimeToHHMM(reserveTime: string): string | null {
  const startTimePart = reserveTime.split(' - ')[0].trim();
  const timeOnly = startTimePart.split(' @ ')[0].trim();
  const timeMatch = timeOnly.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)/i);
  if (!timeMatch) return null;

  let hours = parseInt(timeMatch[1]);
  const minutes = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
  const period = timeMatch[3].toUpperCase();

  if (period === 'PM' && hours !== 12) hours += 12;
  else if (period === 'AM' && hours === 12) hours = 0;

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

function filterSchedulingExclusions(exclusions: Array<any>): Array<any> {
  return exclusions.filter((blockhold: any) => blockhold.reasontype === 1 || blockhold.reasontype === 3);
}

/**
 * ScreenM - Appointment time selection screen.
 *
 * Allows the user to select a date and time for their appointment.
 */
const ScreenM: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppState();
  const [calendarMonth, setCalendarMonth] = useState<number | null>(null);
  const [calendarYear, setCalendarYear] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [allAppointments, setAllAppointments] = useState<AppointmentTime[]>([]);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [timeSlots, setTimeSlots] = useState<AppointmentTime[]>([]);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [hasDatesInCurrentMonth, setHasDatesInCurrentMonth] = useState<boolean>(false);
  const [blockedTimeslots, setBlockedTimeslots] = useState<Array<any>>([]);
  const [isLoadingBlockedTimeslots, setIsLoadingBlockedTimeslots] = useState<boolean>(true);
  const [showBookedDialog, setShowBookedDialog] = useState<boolean>(false);
  const [isCheckingSlot, setIsCheckingSlot] = useState<boolean>(false);
  const hasFetchedRef = React.useRef<boolean>(false);
  const columnIdRef = React.useRef<number | null>(null);

  // Fetch blocked timeslots once when component mounts (only when columnId is available)
  useEffect(() => {
    const loadBlockedTimeslots = async () => {
      // If we've already fetched for this columnId in this mount, don't fetch again
      if (hasFetchedRef.current && columnIdRef.current === state.columnId) {
        return;
      }

      // If columnId is not available yet, wait
      if (!state.columnId) {
        setIsLoadingBlockedTimeslots(false);
        setBlockedTimeslots([]);
        return;
      }

      // Use today or yesterday as the start date
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      
      // Use yesterday to be safe (in case there are any timezone issues or same-day blocks)
      const startDate = yesterday.toISOString().split('T')[0];

      setIsLoadingBlockedTimeslots(true);
      try {
        const config = getConfig();
        const baseUrl = state.remoteApiUrl || config.remoteApiUrl || 'https://scheduling-api.mindfultherapygroup.com/';
        
        const blocked = await fetchBlockHolds(state.columnId, 'month', startDate, baseUrl);
        const filteredBlocked = filterSchedulingExclusions(blocked);
        setBlockedTimeslots(filteredBlocked);
        hasFetchedRef.current = true;
        columnIdRef.current = state.columnId;
      } catch (error) {
        setBlockedTimeslots([]);
        hasFetchedRef.current = true; // Mark as fetched even on error to prevent retries
        columnIdRef.current = state.columnId;
      } finally {
        setIsLoadingBlockedTimeslots(false);
      }
    };

    loadBlockedTimeslots();

    // Reset the refs when component unmounts so it can fetch again on next mount
    return () => {
      hasFetchedRef.current = false;
      columnIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.columnId, state.remoteApiUrl]); // Depend on columnId and remoteApiUrl

  useEffect(() => {
    const loadAppointmentTimes = async () => {
      try {
        // Wait for loading to complete
        if (state.isLoading) {
          return;
        }
        
        // Get availability data to extract appointment times
        const availabilityData = state.availabilities;
        const selectedRegion = state.stateString;
        const selectedLocation = state.locationCode;
        
        if (availabilityData && availabilityData.length > 0) {
          // Extract appointment times from availability data for the selected region and location
          const appointmentTimes: AppointmentTime[] = [];
          
          const filteredAvailabilities = getAvailabilitiesForScheduling(
            availabilityData, 
            selectedRegion, 
            selectedLocation
          );
          
          filteredAvailabilities.forEach(availability => {
              if (availability.startDateIoi && availability.startTime) {
                // Convert startDateIoi to YYYY-MM-DD format if needed
                const date = new Date(availability.startDateIoi);
                const formattedDate = date.toISOString().split('T')[0];
                
                // Check if this availability is blocked
                const isBlocked = blockedTimeslots.some(blocked =>
                  exclusionOverlapsSlot(formattedDate, availability.startTime, blocked)
                );
                
                // Skip blocked availabilities
                if (isBlocked) {
                  return;
                }
                
                // Create time range format (1 hour duration)
                const startTime = availability.startTime;
                const timeRange = createTimeRange(startTime, availability.locationName || 'Location');
                
                // Convert startDateIoi and startTime to AppointmentTime format
                const appointmentTime: AppointmentTime = {
                  id: `${availability.contactId}-${availability.providerCode}-${formattedDate}-${availability.startTime}-${Math.random().toString(36).substr(2, 9)}`,
                  date: formattedDate,
                  time: timeRange
                };
                
                
                appointmentTimes.push(appointmentTime);
              }
            });
          
          setAllAppointments(appointmentTimes);
          
          // Extract unique dates (already in YYYY-MM-DD format)
          const dates = Array.from(new Set(appointmentTimes.map(a => a.date)));
      setAvailableDates(dates);
          
      // Always set calendar to earliest available month if calendar month/year are not set
      // or if we have dates and need to update
      if (dates.length > 0) {
            // Sort dates to get the earliest one
            const sortedDates = dates.sort();
            const earliestDate = sortedDates[0];
            
            // If calendar month/year are null, always set them to the earliest available month
            if (calendarMonth === null || calendarYear === null) {
              const d = new Date(earliestDate + 'T00:00:00');
              setCalendarMonth(d.getMonth());
              setCalendarYear(d.getFullYear());
            }
            
      // Set initial selected date, month, year if not set, or update if we have a preferred day from sessionData
      const shouldUpdateDate = !selectedDate || state.sessionData;
      if (shouldUpdateDate) {
            // Try to get cs_day from ioiPayload to fast-forward to matching day
            let preferredDayOfWeek: number | null = null;
            if (state.sessionData && state.sessionData.data) {
              try {
                // Try to get ioiPayload from data.data (parsed JSON string)
                if (state.sessionData.data.data) {
                  let parsedData;
                  if (typeof state.sessionData.data.data === 'string') {
                    parsedData = JSON.parse(state.sessionData.data.data);
                  } else {
                    parsedData = state.sessionData.data.data;
                  }
                  
                  if (parsedData.ioiPayload && parsedData.ioiPayload.cs_day) {
                    const dayName = parsedData.ioiPayload.cs_day;
                    preferredDayOfWeek = getDayOfWeekNumber(dayName);
                  }
                }
                
                // Try to get ioiPayload from data.ioiPayload (JSON string)
                if (preferredDayOfWeek === null && state.sessionData.data.ioiPayload) {
                  let ioiPayload;
                  if (typeof state.sessionData.data.ioiPayload === 'string') {
                    ioiPayload = JSON.parse(state.sessionData.data.ioiPayload);
                  } else {
                    ioiPayload = state.sessionData.data.ioiPayload;
                  }
                  
                  if (ioiPayload.cs_day) {
                    const dayName = ioiPayload.cs_day;
                    preferredDayOfWeek = getDayOfWeekNumber(dayName);
                  }
                }
              } catch (error) {
              }
            }
            
            // If we have a preferred day of week, find the first matching date
            let dateToSelect = earliestDate;
            if (preferredDayOfWeek !== null) {
              const matchingDate = sortedDates.find(date => {
                const d = new Date(date + 'T00:00:00'); // Add time to avoid timezone issues
                const dayOfWeek = d.getDay();
                return dayOfWeek === preferredDayOfWeek;
              });
              if (matchingDate) {
                dateToSelect = matchingDate;
              }
            }
            
            setSelectedDate(dateToSelect);
            // Update calendar month/year to match the selected date
            const d = new Date(dateToSelect + 'T00:00:00');
            setCalendarMonth(d.getMonth());
            setCalendarYear(d.getFullYear());
          }
          }
        } else {
          // No availability data - set empty arrays
          setAllAppointments([]);
          setAvailableDates([]);
        }
      } catch (error) {
        setAllAppointments([]);
        setAvailableDates([]);
      }
    };

    // Add a small delay to ensure availability data is loaded
    const timeoutId = setTimeout(loadAppointmentTimes, 500);
    
    return () => clearTimeout(timeoutId);
  }, [state.stateString, state.locationCode, state.availabilities, state.isLoading, state.sessionData, blockedTimeslots]);

  useEffect(() => {
    // Only update time slots when selectedDate changes, don't touch calendar navigation
    const filteredSlots = allAppointments.filter(a => a.date === selectedDate);
    
    // Sort time slots in ascending order by time
    const sortedSlots = filteredSlots.sort((a, b) => {
      // Parse time strings in format "3pm - 4pm @ Location" or "3:00pm - 4:00pm @ Location"
      const parseTime = (timeStr: string) => {
        // Extract the start time (first part before " - ")
        const startTimePart = timeStr.split(' - ')[0].trim();
        
        // Remove "@ Location" if present
        const timeOnly = startTimePart.split(' @ ')[0].trim();
        
        // Parse formats like "3pm", "3:00pm", "10am", "10:30am"
        const timeMatch = timeOnly.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)/i);
        if (!timeMatch) return 0;
        
        let hours = parseInt(timeMatch[1]);
        const minutes = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
        const period = timeMatch[3].toUpperCase();
        
        // Convert to 24-hour format
        if (period === 'PM' && hours !== 12) {
          hours += 12;
        } else if (period === 'AM' && hours === 12) {
          hours = 0;
        }
        
        return hours * 60 + minutes;
      };
      
      return parseTime(a.time) - parseTime(b.time);
    });
    
    setTimeSlots(sortedSlots);
    // Auto-select the first time slot if only one is available
    if (sortedSlots.length === 1) {
      setSelectedTime(sortedSlots[0].time);
    } else {
      setSelectedTime(null);
    }
  }, [selectedDate, allAppointments]);

  // New useEffect to handle month changes and auto-select appropriate date
  useEffect(() => {
    if (calendarMonth !== null && calendarYear !== null && availableDates.length > 0) {
      // Check if the currently selected date is valid and in the current month
      if (selectedDate) {
        const selectedDateObj = new Date(selectedDate + 'T00:00:00');
        const isSelectedDateInCurrentMonth = selectedDateObj.getMonth() === calendarMonth && 
                                            selectedDateObj.getFullYear() === calendarYear;
        const isSelectedDateAvailable = availableDates.includes(selectedDate);
        
        // If the selected date is valid and in the current month, don't override it
        if (isSelectedDateInCurrentMonth && isSelectedDateAvailable) {
          setHasDatesInCurrentMonth(true);
          return;
        }
      }
      
      // Find available dates in the current month
      const currentMonthDates = availableDates.filter(date => {
        const d = new Date(date + 'T00:00:00');
        return d.getMonth() === calendarMonth && d.getFullYear() === calendarYear;
      }).sort();
      
      if (currentMonthDates.length > 0) {
        // Check if we're going backwards by comparing with current selected date
        let isGoingBackwards = false;
        if (selectedDate) {
          const currentSelectedDate = new Date(selectedDate + 'T00:00:00');
          isGoingBackwards = currentSelectedDate.getMonth() > calendarMonth || 
                            (currentSelectedDate.getMonth() === calendarMonth && currentSelectedDate.getFullYear() > calendarYear);
        }
        
        let dateToSelect;
        if (isGoingBackwards) {
          // When going backwards, select the LAST date in the month
          dateToSelect = currentMonthDates[currentMonthDates.length - 1];
        } else {
          // When going forwards, select the FIRST date in the month
          dateToSelect = currentMonthDates[0];
        }
        
        setSelectedDate(dateToSelect);
        setHasDatesInCurrentMonth(true);
      } else {
        setSelectedDate('');
        setHasDatesInCurrentMonth(false);
      }
    } else {
      setHasDatesInCurrentMonth(false);
    }
  }, [calendarMonth, calendarYear, availableDates, selectedDate]);

  useEffect(() => {
    // ...
  }, [calendarMonth, calendarYear]);

  const handleReserve = async (time?: string, date?: string) => {
    const reserveTime = time || selectedTime;
    const reserveDate = date || selectedDate;
    if (!reserveDate || !reserveTime || isCheckingSlot) {
      return;
    }

    const slotTime = parseReserveTimeToHHMM(reserveTime);
    if (!slotTime) {
      return;
    }

    setIsCheckingSlot(true);
    try {
      const config = getConfig();
      const baseUrl = state.remoteApiUrl || config.remoteApiUrl || 'https://scheduling-api.mindfultherapygroup.com/';

      // Use week view — AMD's day view is unreliable; week with same start/end still returns the day
      const dayExclusions = await fetchBlockHolds(
        state.columnId,
        'week',
        reserveDate,
        baseUrl,
        reserveDate
      );
      const filteredDayExclusions = filterSchedulingExclusions(dayExclusions);
      const hasOverlap = filteredDayExclusions.some(exclusion =>
        exclusionOverlapsSlot(reserveDate, slotTime, exclusion)
      );

      if (hasOverlap) {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        const monthStartDate = yesterday.toISOString().split('T')[0];
        const refreshedBlocked = await fetchBlockHolds(state.columnId, 'month', monthStartDate, baseUrl);
        setBlockedTimeslots(filterSchedulingExclusions(refreshedBlocked));
        setSelectedTime(null);
        setShowBookedDialog(true);
        return;
      }

      dispatch({ type: 'SET_APPOINTMENT_DATE', payload: reserveDate });
      dispatch({ type: 'SET_APPOINTMENT_TIME', payload: reserveTime });
      
      // Set timer start time and call API
      const currentTime = Date.now();
      dispatch({ type: 'SET_RESERVATION_START_TIME', payload: currentTime });
      
      // Call API to set timer start time
      try {
        await setTimerStart(baseUrl, new Date(currentTime).toISOString());
      } catch (error) {
        // Continue with navigation even if API call fails
      }
      
      // Block the appointment
      try {
        // Get provider code from state
        const providerCode = state.providerId;
        if (!providerCode) {
          navigate('/n');
          return;
        }
        
        // Get location state from state
        const locationState = state.stateString || 'Colorado'; // Default to Colorado if not set
        
        // Convert time to ISO format
        // Parse the selected date and time to create a proper ISO datetime
        const [timeStr, period] = reserveTime.replace(/\s/g, '').split(/(am|pm)/i);
        const [hours, minutes] = timeStr.split(':');
        let hour24 = parseInt(hours);
        
        if (period.toLowerCase() === 'pm' && hour24 !== 12) {
          hour24 += 12;
        } else if (period.toLowerCase() === 'am' && hour24 === 12) {
          hour24 = 0;
        }
        
        // Create ISO datetime string manually without timezone processing
        // Parse the date string (assuming format like "2025-10-19")
        const [year, month, day] = reserveDate.split('-');
        
        // Build the datetime string manually: "YYYY-MM-DDTHH:MM:SS.000Z"
        const isoDateTime = `${year}-${month}-${day}T${hour24.toString().padStart(2, '0')}:${(parseInt(minutes) || 0).toString().padStart(2, '0')}:00.000Z`;
        
        const providerName = getProviderNameFromAvailability(state.availabilities, 'Provider');
        
        // Find the matching availability for the selected date and time to get availabilityId
        // This needs to happen here because selectedAvailability is set on ScreenN, not ScreenM
        let availabilityId: number | undefined = undefined;
        if (state.availabilities && reserveDate && reserveTime) {
          // Find availability that matches the selected date and time
          const matchingAvailability = state.availabilities.find(av => {
            // Check if date matches
            const dateMatch = av.startDateIoi && av.startDateIoi.startsWith(reserveDate);
            if (!dateMatch) return false;
            
            // Check if time matches (extract hour from reserveTime and compare with av.startTime)
            const timeMatch = reserveTime.match(/(\d{1,2})(?::\d{2})?(am|pm)/i);
            if (!timeMatch || !av.startTime) return false;
            
            const userHour = parseInt(timeMatch[1]);
            const userPeriod = timeMatch[2].toLowerCase();
            let userHour24 = userHour;
            if (userPeriod === 'pm' && userHour !== 12) userHour24 += 12;
            if (userPeriod === 'am' && userHour === 12) userHour24 = 0;
            
            const availHour = parseInt(av.startTime.split(':')[0]);
            return userHour24 === availHour;
          });
          
          if (matchingAvailability && matchingAvailability.availabilityId) {
            availabilityId = matchingAvailability.availabilityId;
          } else {
          }
        }
        
        const blockSuccess = await blockAppointment(
          providerCode,
          state.providerId || '',
          state.columnId,
          state.profileId,
          providerName,
          locationState,
          isoDateTime,
          baseUrl,
          availabilityId
        );
        
        if (blockSuccess) {
        } else {
          // Continue with navigation even if blocking fails
        }
      } catch (error) {
        // Continue with navigation even if blocking fails
      }
      
      navigate('/n');
    } finally {
      setIsCheckingSlot(false);
    }
  };

  // Don't render calendar until month/year are set and blocked timeslots are loaded
  if (calendarMonth === null || calendarYear === null || isLoadingBlockedTimeslots) {
    return (
      <div className="screen-bg">
        <div className="main-content-container">
          <div className="mtg-back-btn-align">
            <BackButton onClick={() => navigate(-1)} />
          </div>
          <main className="mtg-screen-main" id="main-content">
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 16px auto' }} />
              <div style={{ color: '#b5b5b5', fontWeight: 500 }} role="status" aria-live="polite">
                {isLoadingBlockedTimeslots ? 'Fetching blocked timeslots…' : 'Loading calendar…'}
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="screen-bg">
      <div className="main-content-container">
        <div className="mtg-back-btn-align">
          <BackButton onClick={() => navigate(-1)} />
        </div>
        <main className="mtg-screen-main" id="main-content">
          {calendarMonth === null || calendarYear === null ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 16px auto' }} />
              <div style={{ color: '#b5b5b5', fontWeight: 500 }} role="status" aria-live="polite">Loading calendar…</div>
            </div>
          ) : (
            <>
              <h2 className="mtg-screen-question" id="time-selection-title" style={{ textAlign: 'center', color: '#185F93', fontWeight: 700, fontSize: '1.35rem', margin: '0 0 24px 0' }}>
                Choose a time
              </h2>
              <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.04)'}}>
                <Calendar
                  year={calendarYear}
                  month={calendarMonth}
                  availableDates={availableDates}
                  selectedDate={selectedDate}
                  onSelectDate={setSelectedDate}
                  onPrevMonth={() => {
                    if (calendarMonth === 0) {
                      setCalendarMonth(11);
                      setCalendarYear((calendarYear || 2024) - 1);
                    } else {
                      setCalendarMonth((calendarMonth || 0) - 1);
                    }
                  }}
                  onNextMonth={() => {
                    if (calendarMonth === 11) {
                      setCalendarMonth(0);
                      setCalendarYear((calendarYear || 2024) + 1);
                    } else {
                      setCalendarMonth((calendarMonth || 0) + 1);
                    }
                  }}
                />
              </div>
              <div style={{ maxWidth: 380, width: '100%' }}>
                <ProgressBar progress={81} />
              </div>
              {hasDatesInCurrentMonth && (
              <div className="mtg-screen-card mt-18" style={{ paddingBottom: 0, paddingTop: 10 }}>
                <div style={{ width: '100%', margin: '0 auto 18px auto', textAlign: 'center' }}>
                  <DateSelector
                    selectedDate={selectedDate}
                    availableDates={availableDates}
                    onSelect={(date) => {
                      setSelectedDate(date);
                      // Update calendar month when date changes
                      const newDate = new Date(date);
                      setCalendarMonth(newDate.getMonth());
                      setCalendarYear(newDate.getFullYear());
                    }}
                  />
                  <TimeSlotSelector
                    timeSlots={timeSlots}
                    onReserve={(time) => { 
                      setSelectedTime(time); 
                      setTimeout(() => handleReserve(time, selectedDate), 0); 
                    }}
                    location={state.location || 'Northgate'}
                  />
                  <button
                    className="mtg-secondary-action-btn"
                    onClick={() => {
                      dispatch({ type: 'SET_LOCATION', payload: '' });
                      navigate('/i');
                    }}
                    style={{ marginTop: '16px' }}
                    aria-label="Pick a different location"
                  >
                    Pick A Different Location
                  </button>
                </div>
              </div>
              )}
            </>
          )}
        </main>
      </div>
      <ErrorModal
        isOpen={showBookedDialog}
        onClose={() => setShowBookedDialog(false)}
        title="Time Unavailable"
        message="Oops — that time has just been booked. Please choose another available date and time."
      />
    </div>
  );
};

export default ScreenM; 